
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import security from '@/common/mixins/security';
  import { getUserRegionsRfIds } from '@/common/utils/userProfile';
  import { getCurrentYear } from '@/common/utils/year';

  import DateActualDataSubtitle from '../components/common/DateActualDataSubtitle.vue';
  import DashboardsChart from '../components/dashboards/DashboardsChart.vue';
  import DashboardsChartsBlock from '../components/dashboards/DashboardsChartsBlock.vue';
  import DashboardsRatingCard from '../components/dashboards/DashboardsRatingCard.vue';
  import DashboardsRatingTooltipContent from '../components/dashboards/DashboardsRatingTooltipContent.vue';
  import DashboardsTables from '../components/dashboards/DashboardsTables.vue';
  import RegionDashboardsAdditionFilterBlock from '../components/dashboards/region-dashboards/RegionDashboardsAdditionFilterBlock.vue';
  import RegionDashboardsBaseInfo from '../components/dashboards/region-dashboards/RegionDashboardsBaseInfo.vue';
  import RegionDashboardsDynamicCards from '../components/dashboards/region-dashboards/RegionDashboardsDynamicCards.vue';
  import RegionDashboardsIndicatorCards from '../components/dashboards/region-dashboards/RegionDashboardsIndicatorCards.vue';
  import RegionDashboardsMainFilterBlock from '../components/dashboards/region-dashboards/RegionDashboardsMainFilterBlock.vue';
  import RegionDashboardsRatingCards from '../components/dashboards/region-dashboards/RegionDashboardsRatingCards.vue';
  import RegionDashboardsRepresentativeAbroadInfo from '../components/dashboards/region-dashboards/RegionDashboardsRepresentativeAbroadInfo.vue';
  import {
    ALL_YEAR_OPTION,
    DASHBOARDS_RATING_CARD_TITLES,
    DEFAULT_DASHBOARDS_FILTER,
    VES_EXPORT_REGION_EFFECTIVNESS_RATING_EXCEL_SORT_ORDER,
  } from '../constants/dashboards';
  import { DASHBOARDS_VES_ACTIONS_NAME, DASHBOARDS_VES_GETTERS_NAME } from '../store/dashboards/name';
  import { REGION_DASHBOARDS_VES_GETTERS_NAME } from '../store/regionDashboards/name';
  import type { TEventsTableFilter, TRatingElement } from '../types/dashboards';
  import {
    TDashboardRegionCalendarFilter,
    TDashboardRegionFilter,
    TDashboardRegionFilterFullFilter,
    TDashboardRegionRepresentativeAbroadFilter,
    TDashboardRegionTableFilter,
  } from '../types/regionDashboards';
  import { getRatingListWithIndex } from '../utils/rating';

  import { DIRECTIVES_GETTERS_NAME } from '@/store/directives';

  export default defineComponent({
    name: 'RegionDashboardsVES',
    components: {
      DashboardsChart,
      RegionDashboardsMainFilterBlock,
      RegionDashboardsBaseInfo,
      RegionDashboardsIndicatorCards,
      RegionDashboardsDynamicCards,
      RegionDashboardsRatingCards,
      DateActualDataSubtitle,
      DashboardsChartsBlock,
      DashboardsTables,
      RegionDashboardsAdditionFilterBlock,
      RegionDashboardsRepresentativeAbroadInfo,
      DashboardsRatingCard,
      DashboardsRatingTooltipContent,
    },
    mixins: [security],
    async created() {
      const { isSubjectRfRole, userRegionRfIds } = getUserRegionsRfIds();

      if (!userRegionRfIds?.includes(Number(this.$route.params.regionRfId)) && isSubjectRfRole && userRegionRfIds[0]) {
        this.redirectToRegionCard(userRegionRfIds[0]);
        return;
      }

      // ? Для администратора ВЭС не работает по ТЗ, поэтому пока закомментировал
      // if (!(isSubjectRfRole && userRegionRfIds[0])) {
      //   await this.checkPermissionAndRedirect(Constants.Permissions.AdminAccess);
      // }

      this.isAdmin = this.isShowData = true;
    },
    data(): {
      filter: TDashboardRegionFilterFullFilter;
      filterWithoutCountry: TDashboardRegionFilter;
      isShowData: boolean;
      isAdmin: boolean;
      calendarParams: TDashboardRegionCalendarFilter;
      tableParams: TDashboardRegionTableFilter;
      plannedEventsFilter: TEventsTableFilter;
      isRatingLoading: boolean;
      ratingAbortController: AbortController;
      exportRegionEffectivnessRatingExcelSortOrder: typeof VES_EXPORT_REGION_EFFECTIVNESS_RATING_EXCEL_SORT_ORDER;
      ratingCardTitles: typeof DASHBOARDS_RATING_CARD_TITLES;
    } {
      return {
        filter: {
          regionRfId: Number(this.$route.params.regionRfId),
          year: this.$route.query?.year ? String(this.$route.query.year) : DEFAULT_DASHBOARDS_FILTER.year,
        },
        filterWithoutCountry: {
          regionRfId: Number(this.$route.params.regionRfId),
          year: this.$route.query?.year ? String(this.$route.query.year) : DEFAULT_DASHBOARDS_FILTER.year,
        },
        isShowData: false,
        isAdmin: false,
        plannedEventsFilter: {},
        calendarParams: {
          regionRfId: Number(this.$route.params.regionRfId),
        },
        tableParams: {
          subjectRfIds: [Number(this.$route.params.regionRfId)],
          includeSystemEventsWithoutRegionFilter: true,
        },
        isRatingLoading: false,
        ratingAbortController: new AbortController(),
        exportRegionEffectivnessRatingExcelSortOrder: VES_EXPORT_REGION_EFFECTIVNESS_RATING_EXCEL_SORT_ORDER,
        ratingCardTitles: DASHBOARDS_RATING_CARD_TITLES,
      };
    },
    computed: {
      getPlannedEventsRegionRfIds(): number[] {
        return this.plannedEventsFilter.subjectRfIds ?? this.tableParams.subjectRfIds ?? [];
      },
      ...mapGetters({
        regionsList: DIRECTIVES_GETTERS_NAME.getRegionsSearchIdList,
        baseRegionInfo: REGION_DASHBOARDS_VES_GETTERS_NAME.getBaseRegionInfo,
        topEffectiveRegions: DASHBOARDS_VES_GETTERS_NAME.getTopEffectiveRegions,
        topEffectiveRegionsRating: DASHBOARDS_VES_GETTERS_NAME.getTopEffectiveRegionsRating,
        topCountryPartners: DASHBOARDS_VES_GETTERS_NAME.getTopCountryPartners,
      }),
      isChartVisible(): boolean {
        return Array.isArray(this.topCountryPartners) && this.topCountryPartners.length > 0;
      },
      getChartTitle() {
        return `Топ-5 стран-партнеров по количеству мероприятий ${this.baseRegionInfo?.genetiveCase ?? ''}`;
      },
      getFilterWithoutCountryKey() {
        return `${this.filter.year}_${this.filter.regionRfId}`;
      },
      getRepresentativeAbroadParamsFilter(): TDashboardRegionRepresentativeAbroadFilter {
        return {
          year: this.filter.year,
          countryId: this.filter.countryId,
        };
      },
      ratingYearFilter(): { year: string } {
        return { year: this.filter.year };
      },
      topEffectiveRegionsRatingWithIndex(): TRatingElement[] {
        return getRatingListWithIndex(this.topEffectiveRegionsRating, this.topEffectiveRegions);
      },
      getTopEffectiveRegionsRatingReverseWithIndex(): TRatingElement[] {
        return [...this.topEffectiveRegionsRatingWithIndex].reverse();
      },
      isShowDynamicInRatingCards(): boolean {
        return this.filter.year !== ALL_YEAR_OPTION.value;
      },
    },
    methods: {
      changePlannedEventsFilter(filter: TEventsTableFilter) {
        this.plannedEventsFilter = filter;
      },
      ...mapActions({
        getTopEffectiveRegions: DASHBOARDS_VES_ACTIONS_NAME.getTopEffectiveRegions,
        getTopEffectiveRegionsRating: DASHBOARDS_VES_ACTIONS_NAME.getTopEffectiveRegionsRating,
      }),
      changeFilter(filter: Partial<TDashboardRegionFilterFullFilter>) {
        const newYear = filter.year ?? String(getCurrentYear());
        this.filter = {
          ...this.filter,
          ...filter,
          year: newYear,
        };
        this.$router.replace({
          query: {
            ...this.$route.query,
            year: newYear,
          },
        });
      },
      redirectToRegionCard(regionRfId: number) {
        this.$router.replace({ name: 'RegionDashboardsVES', params: { regionRfId } });
      },
      async loadRatingData() {
        this.ratingAbortController.abort();
        this.ratingAbortController = new AbortController();

        const payload = {
          params: this.ratingYearFilter,
          abortController: this.ratingAbortController,
        };

        this.isRatingLoading = true;
        try {
          await Promise.all([this.getTopEffectiveRegions(payload), this.getTopEffectiveRegionsRating(payload)]);
        } finally {
          this.isRatingLoading = false;
        }
      },
    },
    watch: {
      regionsList: {
        handler() {
          if (!this.isAdmin) return;

          if (!this.regionsList.map((i: { id: number }) => i.id).includes(Number(this.$route.params.regionRfId))) {
            this.redirectToRegionCard(this.regionsList[0].id);
            return;
          }
        },
      },
      getFilterWithoutCountryKey(value) {
        const [year, regionRfId] = value.split('_');
        this.filterWithoutCountry = {
          year: year,
          regionRfId: Number(regionRfId),
        };
      },
      ratingYearFilter: {
        handler() {
          this.loadRatingData();
        },
        deep: true,
        immediate: true,
      },
    },
  });
